import * as Yup from 'yup'
import { useCallback, useRef, useState, useMemo } from 'react'
import { Formik } from 'formik'
import { useParams, useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { v4 as uuid } from 'uuid'
import {
  isEmpty,
  omit,
  keys,
  filter,
  isNumber,
  includes,
  isNaN,
  compact,
} from 'lodash'

import ClassificationsDetails from 'components/Item/Form/Details/ClassificationsDetails'
import GeneralDetails from 'components/Item/Form/Details/GeneralDetails'
import TaxDetails from 'components/Item/Form/Details/TaxDetails'
import Menus from 'components/Item/Form/Details/Menus'
import MenusDrawer from 'components/Item/Form/Drawers/Menus'
import Item from 'components/Item/Form/Drawers/Item'
import PriceLevelDetails from 'components/Item/Form/Details/PriceLevelDetails'
import MinMaxSettings from 'components/Item/Form/Details/MinMaxSettings'
import HasModifierGroups from 'components/Item/Form/Details/HasModifierGroups'
import BelongsToModifierGroups from 'components/Item/Form/Details/BelongsToModifierGroups'

import { formatMoney, dollarsToCents } from 'utils/formatters'
import {
  transformItemMappingModifierPreferences,
  transformHasAndBelongsModiferGroups,
  calculateTaxRateInitialValue,
  calculateTaxRateToSubmit,
} from 'utils/items'

import {
  createGlobalItem,
  removeGlobalItem,
  updateGlobalItem,
} from 'store/Items/actions'
import { getItemFromPagination } from 'store/Items/selector'
import { getPriceLevelsPerSalesEventType } from 'store/PriceLevels/selectors'
import { getSalesEventTypes } from 'store/SalesEventTypes/selectors'
import {
  updatePriceLevel,
  createPriceLevel,
} from 'store/PriceLevels/actions'
import {
  getMinorCategories,
  getMajorCategories,
  getModifierGroups,
} from 'store/Categories/selectors'

const ItemForm = () => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [formStep, setFormStep] = useState('itemsForm')

  const { itemId } = useParams()
  const history = useHistory()
  const dispatch = useDispatch()

  const salesEventTypes = useSelector((state) =>
    getSalesEventTypes(state)
  )
  const item = useSelector((state) =>
    getItemFromPagination(state, itemId)
  )
  const modifierGroups = useSelector(getModifierGroups)

  // Build belongs to modifier groups and mappings.
  const belongsToModifierGroups = useMemo(
    () =>
      filter(modifierGroups, (modifierGroup) =>
        includes(item?.itemCategoryUuids, modifierGroup.uuid)
      ),
    [item?.itemCategoryUuids, modifierGroups]
  )
  // Map the modifier groups to their uuids
  const belongsToModifierGroupUuids = useMemo(
    () =>
      belongsToModifierGroups.reduce((acc, modifierGroup) => {
        acc.push(modifierGroup.uuid)
        return acc
      }, []),
    [belongsToModifierGroups]
  )

  // Build has modifier groups and mappings.
  const { hasModifierGroups } = useMemo(
    () =>
      transformItemMappingModifierPreferences(
        item?.itemMappingPreferences,
        modifierGroups
      ),
    [item?.itemMappingPreferences, modifierGroups]
  )

  const currentVenueUuid = useSelector(
    (state) => state.venues.selectedId
  )
  const [showValidationErrors, setShowValidationErrors] =
    useState(false)
  const minorCategories = useSelector(getMinorCategories)
  const majorCategories = useSelector(getMajorCategories)

  const menus = useSelector((state) => state?.menus?.all)

  const priceLevelsPerSalesEventType = useSelector((state) =>
    getPriceLevelsPerSalesEventType(state)
  )

  const itemIdIsNew = itemId === 'new'

  const priceLevelsWithOverridesIDs = priceLevelsPerSalesEventType
    .filter((priceLevel) => priceLevel.itemUuid === item.uuid)
    .map((priceLevel) => priceLevel.salesEventTypeId)

  const priceLevelsWithoutOverrides = salesEventTypes.filter(
    (salesEventType) =>
      !priceLevelsWithOverridesIDs.includes(salesEventType.uuid)
  )

  const newPriceLevelFields = priceLevelsWithoutOverrides.map(
    (priceLevelsWithoutOverrides) => {
      return {
        itemUuid: item.uuid,
        name: priceLevelsWithoutOverrides.name,
        price: '',
        salesEventTypeId: priceLevelsWithoutOverrides.uuid,
      }
    }
  )

  const savedPriceLevelFields = priceLevelsPerSalesEventType.filter(
    (priceLevel) => priceLevel.itemUuid === item.uuid
  )

  const priceLevelFields = [
    ...savedPriceLevelFields,
    ...newPriceLevelFields,
  ]

  const renderSalesEventTypeInitialValues = priceLevelFields.reduce(
    (acc, item) => {
      const price = new Intl.NumberFormat('en-US', {
        currency: 'USD',
      }).format(formatMoney(item.price))
      return {
        ...acc,
        [item.name.toLowerCase()]: price,
      }
    },
    {}
  )

  const taxRate = calculateTaxRateInitialValue(
    item?.taxable,
    item?.taxInclusive,
    item?.rate
  )

  const initialValues = {
    alcohol: item?.alcohol ?? false,
    autoAdvance: item?.autoAdvance ?? false,
    barcode: item?.barcode ?? '',
    deletedAt: item?.deletedAt !== null,
    description: item?.description ?? '',
    discountable: item?.discountable ?? false,
    displayName: item?.displayName ?? '',
    inventoryState: item?.inventoryState ?? '',
    itemCategoryUuids: item?.itemCategoryUuids ?? [],
    menuUuids: item?.menuUuids ?? [],
    name: item?.name ?? '',
    price: item?.price ?? '',
    productSku: item?.productSku ?? '',
    reportingCategoryUuid: item?.reportingCategoryUuid ?? [],
    modifierGroupsUuids: item?.modifierGroupsUuids ?? '',
    specialType: item?.specialType || null,
    taxable: item?.taxable ?? false,
    taxInclusive: item?.taxInclusive ?? false,
    customerAttributes: {
      inventoryClassification:
        item?.customerAttributes?.inventoryClassification ?? null,
    },
    belongsToModifierGroups: belongsToModifierGroups,
    hasModifierGroups: hasModifierGroups,
    ...renderSalesEventTypeInitialValues,
    ...taxRate,
  }

  const sectionRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ]

  const validationSchema = Yup.object().shape({
    displayName: Yup.string().required('Required'),
    inventoryState: Yup.string().required('Required'),
    itemCategoryUuids: Yup.array().min(1).required('Required'),
    menuUuids: Yup.array().min(1).required('Required'),
    name: Yup.string().required('Required'),
    price: Yup.number().min(0).required('Required'),
    productSku: Yup.string().required('Required'),
    reportingCategoryUuid: Yup.string().required('Required'),
  })

  const onDelete = useCallback(() => {
    dispatch(removeGlobalItem(item.externalRefId)).then(() =>
      history.push('/items')
    )
  }, [dispatch, history])

  const disableUpdateButton = (dirty, errors) => {
    const disableButton =
      !dirty || (showValidationErrors && !isEmpty(errors))

    return disableButton
  }

  const openArchiveItemModal = (event) => {
    event.preventDefault()
    setShowDeleteDialog(true)
  }

  const onArchive = (e) => {
    const archiveItemParams = [
      {
        deletedAt: new Date().toISOString(),
        uuid: item.uuid,
      },
    ]
    dispatch(updateGlobalItem(archiveItemParams)).then(() =>
      history.push('/items')
    )
  }

  const onSubmit = (values) => {
    const itemUuid = itemIdIsNew ? uuid() : item.uuid
    let itemParams = []

    // remove unnecessary values from formik's initialValues
    const filteredValues = omit(
      values,
      'reportingCategoryUuid',
      'belongsToModifierGroups',
      'hasModifierGroups',
      'taxableRate',
      'taxInclusiveRate'
    )

    const itemMappingPreferences =
      transformHasAndBelongsModiferGroups(
        {
          belongsToModifierGroups: values.belongsToModifierGroups,
          hasModifierGroups: values.hasModifierGroups,
        },
        item.itemMappingPreferences
      )

    const filteredModifierItemCategoryUuids = compact(
      values.hasModifierGroups.map((item) => item.modifierGroupUuid)
    )

    const modifierItemCategoryUuids = !isEmpty(
      filteredModifierItemCategoryUuids
    )
      ? {
          modifierItemCategoryUuids:
            filteredModifierItemCategoryUuids,
        }
      : {}

    const rate = calculateTaxRateToSubmit(
      values.taxable,
      values.taxableRate,
      values.taxInclusive,
      values.taxInclusiveRate
    )

    itemParams = [
      ...itemParams,
      {
        ...filteredValues,
        ...modifierItemCategoryUuids,
        rate,
        uuid: itemUuid,
        itemMappingPreferences,
        itemCategoryUuids: [
          ...values.itemCategoryUuids,
          ...values.belongsToModifierGroupUuids,
          values.reportingCategoryUuid,
        ],
      },
    ]

    // Price levels are sent to different endpoint
    const itemParamsWithoutEventSalesTypes = itemParams.map(
      (itemParam) =>
        omit(itemParam, keys(renderSalesEventTypeInitialValues))
    )

    const salesEventTypeToSave = priceLevelsPerSalesEventType
      .filter((priceLevel) => priceLevel.itemUuid === item.uuid)
      .map((priceLevelsPerSalesEventType) => ({
        uuid: priceLevelsPerSalesEventType.itemPropertyOverridesUuid,
        itemUuid: item.uuid,
        wholePrice: dollarsToCents(
          values[priceLevelsPerSalesEventType.name.toLowerCase()]
        ),
        salesEventTypeId:
          priceLevelsPerSalesEventType.salesEventTypeId,
      }))

    const updatedsalesEventTypeIds = salesEventTypeToSave.map(
      (salesEventTypeToSave) => salesEventTypeToSave.salesEventTypeId
    )

    const filteredsalesEventTypeToCreate = salesEventTypes.filter(
      (salesEventType) =>
        !includes(updatedsalesEventTypeIds, salesEventType.uuid)
    )

    const salesEventTypeToCreate = filteredsalesEventTypeToCreate
      .map((salesEventTypeToCreate) => ({
        ...salesEventTypeToCreate,
        wholePrice: dollarsToCents(
          values[salesEventTypeToCreate.name.toLowerCase()]
        ),
        itemUuid: item.uuid,
        salesEventTypeId: salesEventTypeToCreate.uuid,
        uuid: uuid(),
      }))
      .filter(
        (salesEventTypeToCreate) =>
          isNumber(salesEventTypeToCreate.wholePrice) &&
          !isNaN(salesEventTypeToCreate.wholePrice)
      )

    const salesEventTypeToUpdate = filter(
      salesEventTypeToSave,
      (salesEventType) => !isEmpty(salesEventType.uuid)
    ).filter(
      (salesEventTypeToCreate) =>
        isNumber(salesEventTypeToCreate.wholePrice) &&
        !isNaN(salesEventTypeToCreate.wholePrice)
    )

    const onCreatePriceLevel =
      !isEmpty(salesEventTypeToCreate) && !itemIdIsNew
        ? dispatch(
            createPriceLevel(
              {
                itemPropertyOverrides: salesEventTypeToCreate,
              },
              currentVenueUuid
            )
          )
        : Promise.resolve()

    const onUpdatePriceLevel = !isEmpty(salesEventTypeToUpdate)
      ? dispatch(
          updatePriceLevel(
            {
              itemPropertyOverrides: salesEventTypeToUpdate,
            },
            currentVenueUuid
          )
        )
      : Promise.resolve()

    const onCreateItem = itemIdIsNew
      ? dispatch(
          createGlobalItem(
            currentVenueUuid,
            itemParamsWithoutEventSalesTypes[0],
            salesEventTypeToCreate
          )
        )
      : dispatch(updateGlobalItem(itemParamsWithoutEventSalesTypes))

    Promise.all([
      onCreatePriceLevel,
      onUpdatePriceLevel,
      onCreateItem,
    ]).then(() => history.push('/items'))
  }

  const settings = [
    {
      Component: GeneralDetails,
      ref: sectionRefs[0],
      text: 'info',
      url: '#info',
    },
    {
      Component: Menus,
      showValidationErrors,
      menus,
      setFormStep,
      ref: sectionRefs[1],
      text: 'menus',
      url: '#menus',
    },
    {
      Component: PriceLevelDetails,
      ref: sectionRefs[2],
      text: 'price',
      url: '#price',
    },
    {
      Component: TaxDetails,
      ref: sectionRefs[3],
      text: 'tax',
      url: '#tax',
    },
    {
      majorCategories,
      minorCategories,
      modifierGroups,
      isItemModifier: item?.modifier ?? false,
      Component: ClassificationsDetails,
      ref: sectionRefs[4],
      text: 'classifications',
      url: '#classifications',
    },
    {
      Component: MinMaxSettings,
      ref: sectionRefs[5],
      text: 'Min/Max Settings',
      url: '#minMaxSettings',
    },
    {
      Component: HasModifierGroups,
      ref: sectionRefs[6],
      text: 'Has Modifier Groups',
      url: '#hasModifierGroups',
    },
    {
      modifierGroups,
      belongsToModifierGroupUuids,
      Component: BelongsToModifierGroups,
      ref: sectionRefs[7],
      text: 'Belongs to Modifier Groups',
      url: '#belongsToModifierGroups',
    },
  ]

  const modalBody = (
    <p className="item-settings__modal-confirmation-body">
      Are you sure? Archiving this Item will deactivate it and cannot
      be reversed.
    </p>
  )
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values) => onSubmit(values)}
      validationSchema={validationSchema}
    >
      {({ dirty, errors }) => (
        <>
          {formStep === 'itemsForm' && (
            <Item
              settings={settings}
              showValidationErrors={showValidationErrors}
              setShowDeleteDialog={setShowValidationErrors}
              onArchive={onArchive}
              modalBody={modalBody}
              showDeleteDialog={showDeleteDialog}
              setShowValidationErrors={setShowValidationErrors}
              disableUpdateButton={disableUpdateButton}
              dirty={dirty}
              errors={errors}
              itemIdIsNew={itemIdIsNew}
            />
          )}
          {formStep === 'menusDrawer' && (
            <MenusDrawer setFormStep={setFormStep} />
          )}
        </>
      )}
    </Formik>
  )
}

export default ItemForm
